import { CSSProperties } from 'react'

import { ACTION_TYPE, ROUTE_CONDITION_TYPE } from '@/types/action'
import { AVAILABLE_LANGUAGES } from '@/types/language'
import { ModuleConditionalRoute, StepConditionalRoute } from '@/types/route'

export enum COMPONENT_TYPE {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  BUTTON = 'BUTTON',
  RADIO_GROUP = 'RADIO_GROUP',
  VIDEO = 'VIDEO',
  INPUT = 'INPUT',
  SELECT = 'SELECT',
  PHONE_NUMBER = 'PHONE_NUMBER',
  COUNTRY_SELECT = 'COUNTRY_SELECT',
  CHECKBOX_GROUP = 'CHECKBOX_GROUP',
  ALERT = 'ALERT',

  ROW = 'ROW'
}

export enum TEXT_ELEMENT {
  TITLE = 'title', // h1
  BODY = 'body' // p
}

export enum TEXT_JUSTIFY {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right'
}

// <h1>hey!</h1>
export type TextComponent = {
  id: string
  componentType: COMPONENT_TYPE.TEXT
  textKey: string // phrase key
  element: TEXT_ELEMENT
  justify?: TEXT_JUSTIFY
}

export enum INPUT_COMPONENT_TYPE {
  TEXT = 'text',
  PASSWORD = 'password',
  EMAIL = 'email',
  NUMBER = 'number',
  DATE = 'date',
  CHECKBOX = 'checkbox',
  TEXTAREA = 'textarea'
}

// <input type="text" />
export type InputComponent = {
  id: string
  componentType: COMPONENT_TYPE.INPUT
  required?: boolean
}

export type NumberOrDateInputComponent = InputComponent & {
  textKey: string // phrase key (label)
  // In case of number or date
  type: INPUT_COMPONENT_TYPE.NUMBER | INPUT_COMPONENT_TYPE.DATE
  placeholder?: string // phrase key
  min?: number
  max?: number
}

export type TextInputComponent = InputComponent & {
  textKey: string // phrase key (label)
  // In case of text, password or email
  type: INPUT_COMPONENT_TYPE.TEXT | INPUT_COMPONENT_TYPE.PASSWORD | INPUT_COMPONENT_TYPE.EMAIL
  placeholder?: string // phrase key
  minLength?: number
  maxLength?: number
}

export type CheckboxInputComponent = InputComponent & {
  textKey: string // phrase key (label)
  // In case of checkbox
  type: INPUT_COMPONENT_TYPE.CHECKBOX
  checked?: boolean
}

export type TextareaInputComponent = InputComponent & {
  textKey?: string // phrase key (label)

  type: INPUT_COMPONENT_TYPE.TEXTAREA
  placeholder?: string // phrase key
  minLength?: number
  maxLength?: number
}

// <img src="..." />
export type ImageComponent = {
  id: string
  componentType: COMPONENT_TYPE.IMAGE
  src: string
  style?: CSSProperties
}

// <video src="..." />
export type VideoComponent = {
  id: string
  componentType: COMPONENT_TYPE.VIDEO
  src: string
}

export enum BUTTON_ACTION_TYPE {
  NEXT_STEP = 'NEXT_STEP',
  NEXT_MODULE = 'NEXT_MODULE',
  OPEN_MODAL = 'OPEN_MODAL',
  CLOSE_MODAL = 'CLOSE_MODAL',
  BACK = 'BACK',
  SET_LANGUAGE = 'SET_LANGUAGE',
  OPEN_LINK = 'OPEN_LINK',
  FINISH_CONSULTATION_FORM = 'FINISH_CONSULTATION_FORM',
  CREATE_CONSULTATION = 'CREATE_CONSULTATION',
  RELOAD_PAGE = 'RELOAD_PAGE'
}

export type PreviousAction =
  | {
      action:
        | ACTION_TYPE.CREATE_USER
        | ACTION_TYPE.UPDATE_USER
        | ACTION_TYPE.SET_CHOICE
        | ACTION_TYPE.SET_REMINDER_PHARMACY
        | ACTION_TYPE.SET_REMINDER_PHYSICIAN
        | ACTION_TYPE.SET_REMINDER_WEEKLY
        | ACTION_TYPE.SET_USER_INFO
        | ACTION_TYPE.CREATE_CONSULTATION
        | ACTION_TYPE.CREATE_FOLLOW_UP_CONSULTATION
        | ACTION_TYPE.RESET_TRACES
    }
  | {
      action: ACTION_TYPE.GET_ROUTE_CONDITIONS
      type: ROUTE_CONDITION_TYPE
    }

// What to do when the user clicks on the button. Can navigate to another step (same module) or another module
export type ButtonAction<T> = T extends BUTTON_ACTION_TYPE.BACK
  ? {
      type: BUTTON_ACTION_TYPE.BACK // Previous step if not first step, and previous module (onBackAction) if first step
    }
  : T extends BUTTON_ACTION_TYPE.NEXT_STEP
  ? {
      type: BUTTON_ACTION_TYPE.NEXT_STEP

      // Navigate to another step
      routes: StepConditionalRoute[]
      previousActions?: PreviousAction[]
    }
  : T extends BUTTON_ACTION_TYPE.NEXT_MODULE
  ? {
      type: BUTTON_ACTION_TYPE.NEXT_MODULE

      // Navigate to another module
      routes: ModuleConditionalRoute[]
      previousActions?: PreviousAction[]
    }
  : T extends BUTTON_ACTION_TYPE.OPEN_MODAL
  ? {
      type: BUTTON_ACTION_TYPE.OPEN_MODAL
      modalId: string
    }
  : T extends BUTTON_ACTION_TYPE.CLOSE_MODAL
  ? {
      type: BUTTON_ACTION_TYPE.CLOSE_MODAL
    }
  : T extends BUTTON_ACTION_TYPE.SET_LANGUAGE
  ? {
      type: BUTTON_ACTION_TYPE.SET_LANGUAGE
      language: AVAILABLE_LANGUAGES
    }
  : T extends BUTTON_ACTION_TYPE.OPEN_LINK
  ? {
      type: BUTTON_ACTION_TYPE.OPEN_LINK
      languages?: {
        [key in AVAILABLE_LANGUAGES]?: string
      }
      href: string
    }
  : T extends BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM
  ? {
      type: BUTTON_ACTION_TYPE.FINISH_CONSULTATION_FORM
      previousActions?: PreviousAction[]
    }
  : T extends BUTTON_ACTION_TYPE.RELOAD_PAGE
  ? {
      type: BUTTON_ACTION_TYPE.RELOAD_PAGE
      previousActions?: PreviousAction[]
    }
  : never

// <button onClick={buttonAction()}>hey!</button>
export enum BUTTON_TYPES {
  PRIMARY = 'primary',
  SECONDARY = 'secondary'
}
export type ButtonComponent = {
  id: string
  componentType: COMPONENT_TYPE.BUTTON
  textKey: string // phrase key

  type?: BUTTON_TYPES

  buttonAction: ButtonAction<BUTTON_ACTION_TYPE>
}

export type SelectOptionComponent = {
  id: string
  textKey: string // phrase key

  value: string
}

// <select />
export type SelectComponent = {
  id: string
  componentType: COMPONENT_TYPE.SELECT
  textKey: string // phrase key
  placeholder?: string // phrase key

  options: SelectOptionComponent[]
  required?: boolean
}

// <input type="radio" />
export type RadioOptionComponent = {
  id: string
  textKey: string // phrase key

  value: string

  buttonAction?: ButtonAction<BUTTON_ACTION_TYPE.NEXT_STEP | BUTTON_ACTION_TYPE.NEXT_MODULE | BUTTON_ACTION_TYPE.SET_LANGUAGE> // Call on radio button click -> Can be used to conditional step/module navigation
}

// <fieldset />
export type RadioGroupComponent = {
  id: string
  componentType: COMPONENT_TYPE.RADIO_GROUP
  textKey?: string // phrase key

  options: RadioOptionComponent[]
  required?: boolean
}

export type CountryCode =
  | 'AC'
  | 'AD'
  | 'AE'
  | 'AF'
  | 'AG'
  | 'AI'
  | 'AL'
  | 'AM'
  | 'AO'
  | 'AR'
  | 'AS'
  | 'AT'
  | 'AU'
  | 'AW'
  | 'AX'
  | 'AZ'
  | 'BA'
  | 'BB'
  | 'BD'
  | 'BE'
  | 'BF'
  | 'BG'
  | 'BH'
  | 'BI'
  | 'BJ'
  | 'BL'
  | 'BM'
  | 'BN'
  | 'BO'
  | 'BQ'
  | 'BR'
  | 'BS'
  | 'BT'
  | 'BW'
  | 'BY'
  | 'BZ'
  | 'CA'
  | 'CC'
  | 'CD'
  | 'CF'
  | 'CG'
  | 'CH'
  | 'CI'
  | 'CK'
  | 'CL'
  | 'CM'
  | 'CN'
  | 'CO'
  | 'CR'
  | 'CU'
  | 'CV'
  | 'CW'
  | 'CX'
  | 'CY'
  | 'CZ'
  | 'DE'
  | 'DJ'
  | 'DK'
  | 'DM'
  | 'DO'
  | 'DZ'
  | 'EC'
  | 'EE'
  | 'EG'
  | 'EH'
  | 'ER'
  | 'ES'
  | 'ET'
  | 'FI'
  | 'FJ'
  | 'FK'
  | 'FM'
  | 'FO'
  | 'FR'
  | 'GA'
  | 'GB'
  | 'GD'
  | 'GE'
  | 'GF'
  | 'GG'
  | 'GH'
  | 'GI'
  | 'GL'
  | 'GM'
  | 'GN'
  | 'GP'
  | 'GQ'
  | 'GR'
  | 'GT'
  | 'GU'
  | 'GW'
  | 'GY'
  | 'HK'
  | 'HN'
  | 'HR'
  | 'HT'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IL'
  | 'IM'
  | 'IN'
  | 'IO'
  | 'IQ'
  | 'IR'
  | 'IS'
  | 'IT'
  | 'JE'
  | 'JM'
  | 'JO'
  | 'JP'
  | 'KE'
  | 'KG'
  | 'KH'
  | 'KI'
  | 'KM'
  | 'KN'
  | 'KP'
  | 'KR'
  | 'KW'
  | 'KY'
  | 'KZ'
  | 'LA'
  | 'LB'
  | 'LC'
  | 'LI'
  | 'LK'
  | 'LR'
  | 'LS'
  | 'LT'
  | 'LU'
  | 'LV'
  | 'LY'
  | 'MA'
  | 'MC'
  | 'MD'
  | 'ME'
  | 'MF'
  | 'MG'
  | 'MH'
  | 'MK'
  | 'ML'
  | 'MM'
  | 'MN'
  | 'MO'
  | 'MP'
  | 'MQ'
  | 'MR'
  | 'MS'
  | 'MT'
  | 'MU'
  | 'MV'
  | 'MW'
  | 'MX'
  | 'MY'
  | 'MZ'
  | 'NA'
  | 'NC'
  | 'NE'
  | 'NF'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NP'
  | 'NR'
  | 'NU'
  | 'NZ'
  | 'OM'
  | 'PA'
  | 'PE'
  | 'PF'
  | 'PG'
  | 'PH'
  | 'PK'
  | 'PL'
  | 'PM'
  | 'PR'
  | 'PS'
  | 'PT'
  | 'PW'
  | 'PY'
  | 'QA'
  | 'RE'
  | 'RO'
  | 'RS'
  | 'RU'
  | 'RW'
  | 'SA'
  | 'SB'
  | 'SC'
  | 'SD'
  | 'SE'
  | 'SG'
  | 'SH'
  | 'SI'
  | 'SJ'
  | 'SK'
  | 'SL'
  | 'SM'
  | 'SN'
  | 'SO'
  | 'SR'
  | 'SS'
  | 'ST'
  | 'SV'
  | 'SX'
  | 'SY'
  | 'SZ'
  | 'TA'
  | 'TC'
  | 'TD'
  | 'TG'
  | 'TH'
  | 'TJ'
  | 'TK'
  | 'TL'
  | 'TM'
  | 'TN'
  | 'TO'
  | 'TR'
  | 'TT'
  | 'TV'
  | 'TW'
  | 'TZ'
  | 'UA'
  | 'UG'
  | 'US'
  | 'UY'
  | 'UZ'
  | 'VA'
  | 'VC'
  | 'VE'
  | 'VG'
  | 'VI'
  | 'VN'
  | 'VU'
  | 'WF'
  | 'WS'
  | 'XK'
  | 'YE'
  | 'YT'
  | 'ZA'
  | 'ZM'
  | 'ZW'

// Custom component to show phone number
export type PhoneNumberComponent = {
  id: string
  componentType: COMPONENT_TYPE.PHONE_NUMBER
  placeholder?: string // phrase key
  textKey: string // phrase key
  required?: boolean

  defaultCountry?: CountryCode
}

// Custom component for country selection with state
export type CountrySelectComponent = {
  id: string
  componentType: COMPONENT_TYPE.COUNTRY_SELECT

  countryTextKey: string // phrase key (label)
  countryInputPlaceholder?: string // phrase key
  stateTextKey: string // phrase key (label)
  stateInputPlaceholder?: string // phrase key

  required?: boolean
}

// Custom component for checkbox
export type CheckboxOptionComponent = {
  id: string
  textKey: string // phrase key
  value: string
}

// Group of checkboxes for multiple selection
export type CheckboxGroupComponent = {
  id: string
  componentType: COMPONENT_TYPE.CHECKBOX_GROUP

  textKey: string // phrase key (label)
  options: CheckboxOptionComponent[]
  required?: boolean
}

export enum ALERT_TYPE {
  POSITIVE = 'positive',
  NEGATIVE = 'negative'
}

export type AlertComponent = {
  id: string
  componentType: COMPONENT_TYPE.ALERT

  type: ALERT_TYPE
  textKey: string // phrase key
}

type Components =
  | TextComponent
  | ImageComponent
  | VideoComponent
  | ButtonComponent
  | SelectComponent
  | RadioGroupComponent
  | PhoneNumberComponent
  | TextInputComponent
  | NumberOrDateInputComponent
  | CheckboxInputComponent
  | TextareaInputComponent
  | CountrySelectComponent
  | CheckboxGroupComponent
  | AlertComponent

export type Row = {
  id: string
  componentType: COMPONENT_TYPE.ROW
  components: Components[]
}

export type Component = Components | Row

export type FormComponents =
  | InputComponent
  | RadioGroupComponent
  | PhoneNumberComponent
  | SelectComponent
  | CountrySelectComponent
  | CheckboxGroupComponent
